import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Sparkles, CheckCircle2 } from 'lucide-react';
import { getTemplateCategories, generateTasksFromTemplate, getTemplateColorClasses } from '@/constants/taskTemplates';
import { cn } from '@/lib/utils';

/**
 * TaskTemplatesModal - Modal for selecting and creating task templates
 */
export default function TaskTemplatesModal({ isOpen, onClose, onCreateTemplate, currentListId }) {
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  const templates = getTemplateCategories();

  const handleCreateTemplate = async () => {
    if (!selectedTemplate || !currentListId) return;

    setIsCreating(true);
    try {
      const tasks = generateTasksFromTemplate(selectedTemplate, currentListId);
      await onCreateTemplate(tasks, selectedTemplate.name);
      onClose();
    } catch (error) {
      console.error('Error creating template tasks:', error);
    } finally {
      setIsCreating(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 20 }}
          className="relative bg-white dark:bg-slate-900 rounded-2xl shadow-2xl max-w-3xl w-full max-h-[85vh] overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#006D77] to-[#83C5BE] text-white p-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-xl backdrop-blur-sm">
                <Sparkles size={24} />
              </div>
              <div>
                <h2 className="text-2xl font-bold">Task Templates</h2>
                <p className="text-white/90 text-sm mt-0.5">
                  Quick-start your productivity with pre-built task bundles
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-white/20 rounded-lg transition-colors"
              aria-label="Close"
            >
              <X size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="flex-1 overflow-y-auto p-6 dark:bg-slate-900">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {templates.map((template) => {
                const colors = getTemplateColorClasses(template.color);
                const isSelected = selectedTemplate?.id === template.id;

                return (
                  <motion.div
                    key={template.id}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    onClick={() => setSelectedTemplate(template)}
                    className={cn(
                      'relative p-5 rounded-xl border-2 cursor-pointer transition-all',
                      isSelected 
                        ? 'border-[#006D77] bg-[#006D77]/5 dark:bg-[#006D77]/20 shadow-lg' 
                        : 'border-gray-200 dark:border-slate-700 hover:border-[#006D77]/50 hover:shadow-md dark:bg-slate-800'
                    )}
                  >
                    {/* Selection indicator */}
                    {isSelected && (
                      <div className="absolute top-3 right-3">
                        <CheckCircle2 size={24} className="text-[#006D77]" />
                      </div>
                    )}

                    {/* Template icon */}
                    <div className={cn(
                      'inline-flex items-center justify-center w-12 h-12 rounded-xl text-2xl mb-3',
                      colors.bg,
                      colors.border,
                      'border-2'
                    )}>
                      {template.icon}
                    </div>

                    {/* Template info */}
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-1">
                      {template.name}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                      {template.description}
                    </p>

                    {/* Task count */}
                    <div className={cn(
                      'inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-md',
                      colors.bg,
                      colors.text,
                      colors.border,
                      'border'
                    )}>
                      {template.tasks.length} tasks
                    </div>

                    {/* Preview tasks */}
                    {isSelected && (
                      <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        className="mt-4 pt-4 border-t border-gray-200 dark:border-slate-600"
                      >
                        <p className="text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                          Tasks included:
                        </p>
                        <ul className="space-y-1">
                          {template.tasks.map((task, idx) => (
                            <li key={idx} className="text-xs text-gray-600 dark:text-gray-400 flex items-start gap-1.5">
                              <span className="text-[#006D77] mt-0.5">•</span>
                              <span>{task.title}</span>
                            </li>
                          ))}
                        </ul>
                      </motion.div>
                    )}
                  </motion.div>
                );
              })}
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 dark:border-slate-700 p-6 bg-gray-50 dark:bg-slate-800 flex items-center justify-between">
            <div className="text-sm text-gray-600 dark:text-gray-400">
              {selectedTemplate ? (
                <span className="font-medium">
                  {selectedTemplate.tasks.length} tasks will be created
                </span>
              ) : (
                'Select a template to get started'
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 font-medium hover:bg-gray-200 dark:hover:bg-slate-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateTemplate}
                disabled={!selectedTemplate || isCreating}
                className={cn(
                  'px-6 py-2 font-semibold rounded-lg transition-all',
                  selectedTemplate && !isCreating
                    ? 'bg-[#006D77] text-white hover:bg-[#005a63] shadow-lg hover:shadow-xl'
                    : 'bg-gray-300 dark:bg-slate-600 text-gray-500 dark:text-gray-400 cursor-not-allowed'
                )}
              >
                {isCreating ? (
                  <span className="flex items-center gap-2">
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Creating...
                  </span>
                ) : (
                  'Create Tasks'
                )}
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
}
